import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "@/utils/constants";
import { Header } from "@/components/Header";
import { EditLoginDialog } from "@/components/EditLoginDialog";
import { MyJobsDialog } from "@/components/MyJobsDialog";
import { TeamDialog } from "@/components/TeamDialog";
import { JobSheetDialog } from "@/components/JobSheetDialog";
import { EditingQueueDialog } from "@/components/EditingQueueDialog";
import { ApiAccessDialog } from "@/components/ApiAccessDialog";
import { RecentJobsDialog } from "@/components/RecentJobsDialog";
import { TeamStatsDialog } from "@/components/TeamStatsDialog";
import { RotaGrid } from "@/components/RotaGrid";
import { HolidayRequestDialog } from "@/components/HolidayRequestDialog";
import { AddNoteDialog } from "@/components/AddNoteDialog";
import { ViewGate, isViewAuthenticated, getStoredPassword } from "@/components/ViewGate";

// Set view password header globally for all axios requests
const storedPwd = getStoredPassword();
if (storedPwd) {
  axios.defaults.headers.common['X-View-Password'] = storedPwd;
}

// If the saved view password is no longer valid (e.g. it was changed), clear it and return to the login screen
axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail || '';
    if (status === 401 && String(detail).includes('View password') && getStoredPassword()) {
      try {
        localStorage.removeItem('rota_view_token');
        sessionStorage.removeItem('rota_view_token');
        localStorage.removeItem('rota_jobs');
        localStorage.removeItem('rota_team');
      } catch (e) { /* ignore */ }
      delete axios.defaults.headers.common['X-View-Password'];
      window.location.reload();
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

function App() {
  const [viewAuthed, setViewAuthed] = useState(() => isViewAuthenticated());

  const handleViewAuth = useCallback((pwd) => {
    axios.defaults.headers.common['X-View-Password'] = pwd;
    setViewAuthed(true);
  }, []);
  const [editMode, setEditMode] = useState(false);
  const [authPassword, setAuthPassword] = useState("");
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [myJobsDialogOpen, setMyJobsDialogOpen] = useState(false);
  const [jobs, setJobs] = useState(() => {
    try { const c = localStorage.getItem('rota_jobs'); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const [teamMembers, setTeamMembers] = useState(() => {
    try { const c = localStorage.getItem('rota_team'); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [loading, setLoading] = useState(() => {
    try { return !localStorage.getItem('rota_jobs'); } catch { return true; }
  });
  const [pendingChanges, setPendingChanges] = useState({});
  const [showHidden, setShowHidden] = useState(false);
  const [oldManMode, setOldManMode] = useState(false);
  const [jobSheetOpen, setJobSheetOpen] = useState(false);
  const [jobSheetJobId, setJobSheetJobId] = useState(null);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [pendingHolidays, setPendingHolidays] = useState(0);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [sheetStatuses, setSheetStatuses] = useState({});
  const [editingQueueOpen, setEditingQueueOpen] = useState(false);
  const [recentJobsOpen, setRecentJobsOpen] = useState(false);
  const [teamStatsOpen, setTeamStatsOpen] = useState(false);
  const [apiAccessOpen, setApiAccessOpen] = useState(false);
  const [jumpToDate, setJumpToDate] = useState(null);
  const [rotaNotes, setRotaNotes] = useState([]);
  const pendingChangesRef = useRef({});

  const getAuthHeaders = useCallback(() => ({
    'X-Auth-Password': authPassword
  }), [authPassword]);

  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
  }, [pendingChanges]);

  const flushPendingChanges = useCallback(async (changes) => {
    const changesToSave = changes || pendingChangesRef.current;
    if (Object.keys(changesToSave).length === 0) return;
    
    setSaveStatus('saving');
    try {
      const headers = { 'X-Auth-Password': authPassword, 'Content-Type': 'application/json' };
      
      const promises = Object.entries(changesToSave).map(([jobId, updates]) => 
        axios.put(`${API}/jobs/${jobId}`, updates, { headers })
      );
      
      await Promise.all(promises);
      setPendingChanges({});
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      setSaveStatus('idle');
      if (error.response?.status === 401) {
        toast.error('Not authorized. Please enter password.');
        setEditMode(false);
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Invalid assignment');
        fetchData(); // Revert optimistic update
      } else {
        toast.error('Failed to save changes');
      }
      setPendingChanges({});
    }
  }, [authPassword]);

  useEffect(() => {
    if (!editMode) return;
    
    const saveInterval = setInterval(async () => {
      const changes = pendingChangesRef.current;
      if (Object.keys(changes).length > 0) {
        await flushPendingChanges(changes);
      }
    }, 30000);
    
    return () => clearInterval(saveInterval);
  }, [editMode, flushPendingChanges]);

  const unavailableByDate = useMemo(() => jobs.reduce((acc, job) => {
    if (job.is_unavailable && job.unavailable_person) {
      if (!acc[job.date]) acc[job.date] = [];
      if (!acc[job.date].includes(job.unavailable_person)) {
        acc[job.date].push(job.unavailable_person);
      }
    }
    return acc;
  }, {}), [jobs]);

  const fetchNotes = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/notes`);
      setRotaNotes(resp.data);
    } catch (e) { console.error('Failed to fetch notes:', e); }
  }, []);

  const fetchSheetStatuses = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/job-sheets/status`);
      setSheetStatuses(resp.data || {});
    } catch (e) { console.error('Failed to fetch sheet statuses:', e); }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const syncPromise = axios.get(`${API}/sync-check`).catch(() => {});
      
      const [jobsRes, membersRes, notesRes, statusRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/team-members`),
        axios.get(`${API}/notes`).catch(() => ({ data: [] })),
        axios.get(`${API}/job-sheets/status`).catch(() => ({ data: {} })),
      ]);
      setJobs(jobsRes.data);
      setTeamMembers(membersRes.data);
      setRotaNotes(notesRes.data);
      setSheetStatuses(statusRes.data || {});

      try {
        localStorage.setItem('rota_jobs', JSON.stringify(jobsRes.data));
        localStorage.setItem('rota_team', JSON.stringify(membersRes.data));
      } catch (e) { console.error('localStorage write failed:', e); }

      const syncRes = await syncPromise;
      if (syncRes?.data?.imported > 0 || syncRes?.data?.message?.includes('removed')) {
        const [freshJobs, freshMembers] = await Promise.all([
          axios.get(`${API}/jobs`),
          axios.get(`${API}/team-members`)
        ]);
        setJobs(freshJobs.data);
        setTeamMembers(freshMembers.data);
        try {
          localStorage.setItem('rota_jobs', JSON.stringify(freshJobs.data));
          localStorage.setItem('rota_team', JSON.stringify(freshMembers.data));
        } catch (e) { console.error('localStorage write failed:', e); }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewAuthed) fetchData();
  }, [fetchData, viewAuthed]);

  // Fetch pending holiday count
  useEffect(() => {
    if (!viewAuthed) return;
    const fetchPending = async () => {
      try {
        const resp = await axios.get(`${API}/holiday/requests?status=pending`);
        setPendingHolidays(resp.data.length);
      } catch (e) { console.error('Failed to fetch pending holidays:', e); }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [viewAuthed]);

  const handleEnableEdit = (password) => {
    setAuthPassword(password);
    setEditMode(true);
  };

  const handleDisableEdit = async () => {
    if (Object.keys(pendingChanges).length > 0) {
      await flushPendingChanges();
    }
    setEditMode(false);
    setAuthPassword("");
    setPendingChanges({});
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      if (editMode) {
        const response = await axios.post(`${API}/import-ics`, {}, { headers: getAuthHeaders() });
        toast.success(response.data.message);
      } else {
        const response = await axios.get(`${API}/sync-check?force=true`);
        if (response.data.success) {
          toast.success(response.data.message);
        } else {
          toast.error('Sync failed');
        }
      }
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Not authorized. Please enter password.');
        setEditMode(false);
      } else {
        toast.error('Failed to sync calendar');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleAddMember = async (member) => {
    try {
      await axios.post(`${API}/team-members`, member, { headers: getAuthHeaders() });
      toast.success(`Added ${member.name}`);
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Not authorized. Please enter password.');
        setEditMode(false);
      } else {
        toast.error('Failed to add team member');
      }
    }
  };

  const handleDeleteMember = async (memberId) => {
    try {
      await axios.delete(`${API}/team-members/${memberId}`, { headers: getAuthHeaders() });
      toast.success('Team member removed');
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Not authorized. Please enter password.');
        setEditMode(false);
      } else {
        toast.error('Failed to remove team member');
      }
    }
  };

  const handleAddJob = async (job) => {
    try {
      await axios.post(`${API}/jobs`, job, { headers: getAuthHeaders() });
      toast.success('Job added');
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Not authorized. Please enter password.');
        setEditMode(false);
      } else {
        toast.error('Failed to add job');
      }
    }
  };

  const handleUpdateJob = async (jobId, updates) => {
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...updates } : j));
    
    setPendingChanges(prev => ({
      ...prev,
      [jobId]: { ...(prev[jobId] || {}), ...updates }
    }));
    
    setSaveStatus('pending');
  };

  const handleDeleteJob = async (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    const title = job ? (job.event_name || job.title || 'this job') : 'this job';
    if (!window.confirm(`Delete "${title}"?\n\nThis permanently removes it from the rota and cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { headers: getAuthHeaders() });
      toast.success('Job deleted');
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Not authorized. Please enter password.');
        setEditMode(false);
      } else {
        toast.error('Failed to delete job');
      }
    }
  };

  const handleHideJob = async (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    const title = job ? (job.event_name || job.title || 'this job') : 'this job';
    const currentlyHidden = job && (job.is_hidden || job.hidden);
    const action = currentlyHidden ? 'Unhide' : 'Hide';
    const detail = currentlyHidden ? 'It will show on the rota again.' : 'It stays in the system but is hidden from the rota.';
    if (!window.confirm(`${action} "${title}"?\n\n${detail}`)) return;
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/toggle-hide`, {}, { headers: getAuthHeaders() });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, is_hidden: response.data.is_hidden } : j));
      toast.success(response.data.message);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Not authorized. Please enter password.');
        setEditMode(false);
      } else {
        toast.error('Failed to hide job');
      }
    }
  };

  const handleConfirmStaffing = async (jobId) => {
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/confirm-staffing`, {}, { headers: getAuthHeaders() });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, staffing_confirmed: response.data.staffing_confirmed } : j));
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Not authorized. Please enter password.');
        setEditMode(false);
      } else {
        toast.error('Failed to update staffing');
      }
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `WVE_Team_Rota_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rota exported successfully');
    } catch (error) {
      console.error('Failed to export rota:', error);
      toast.error('Failed to export rota');
    }
  };

  if (!viewAuthed) {
    return <ViewGate onAuthenticated={handleViewAuth} />;
  }

  return (
    <div className="min-h-screen bg-[#f9fafb]">
      <Header
        onSync={handleSync}
        syncing={syncing}
        onOpenTeam={() => setTeamDialogOpen(true)}
        saveStatus={saveStatus}
        editMode={editMode}
        onEnableEdit={() => setLoginDialogOpen(true)}
        onDisableEdit={handleDisableEdit}
        onOpenMyJobs={() => setMyJobsDialogOpen(true)}
        pendingCount={Object.keys(pendingChanges).length}
        onSaveNow={() => flushPendingChanges()}
        onOpenHoliday={() => setHolidayDialogOpen(true)}
        pendingHolidays={pendingHolidays}
        onOpenEditingQueue={() => setEditingQueueOpen(true)}
        onOpenRecentJobs={() => setRecentJobsOpen(true)}
        onOpenTeamStats={() => setTeamStatsOpen(true)}
        onOpenApiAccess={() => setApiAccessOpen(true)}
      />
      
      <main className={`max-w-[1800px] mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8 ${oldManMode ? 'old-man-mode' : ''}`}>
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-[#605e5e]" />
          </div>
        ) : (
          <RotaGrid
            jobs={jobs}
            teamMembers={teamMembers}
            onUpdateJob={handleUpdateJob}
            onDeleteJob={handleDeleteJob}
            onAddJob={handleAddJob}
            onHideJob={handleHideJob}
            onConfirmStaffing={handleConfirmStaffing}
            editMode={editMode}
            unavailableByDate={unavailableByDate}
            showHidden={showHidden}
            onToggleShowHidden={(checked) => setShowHidden(checked)}
            oldManMode={oldManMode}
            onOpenJobSheet={(jobId) => { setJobSheetJobId(jobId); setJobSheetOpen(true); }}
            sheetStatuses={sheetStatuses}
            jumpToDate={jumpToDate}
            onJumpHandled={() => setJumpToDate(null)}
            rotaNotes={rotaNotes}
            onAddNote={() => setAddNoteOpen(true)}
            onDeleteNote={async (noteId) => {
              try {
                await axios.delete(`${API}/notes/${noteId}`, { headers: { 'X-Auth-Password': authPassword } });
                toast.success('Note removed');
                fetchNotes();
              } catch { toast.error('Failed to remove note'); }
            }}
          />
        )}
      </main>
      
      {editMode && (
        <TeamDialog
          open={teamDialogOpen}
          onClose={() => setTeamDialogOpen(false)}
          teamMembers={teamMembers}
          onAddMember={handleAddMember}
          onDeleteMember={handleDeleteMember}
          authPassword={authPassword}
          onRefresh={fetchData}
        />
      )}
      
      <EditLoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        onLogin={handleEnableEdit}
      />
      
      <MyJobsDialog
        open={myJobsDialogOpen}
        onClose={() => setMyJobsDialogOpen(false)}
        teamMembers={teamMembers}
        onOpenJobSheet={(jobId) => { setJobSheetJobId(jobId); setJobSheetOpen(true); }}
      />

      <JobSheetDialog
        open={jobSheetOpen}
        onClose={() => setJobSheetOpen(false)}
        jobId={jobSheetJobId}
        editMode={editMode}
        authPassword={authPassword}
        onSheetSent={fetchSheetStatuses}
      />

      <EditingQueueDialog
        open={editingQueueOpen}
        onClose={() => setEditingQueueOpen(false)}
      />

      <RecentJobsDialog
        open={recentJobsOpen}
        onClose={() => setRecentJobsOpen(false)}
        jobs={jobs}
        onJump={(date) => { setRecentJobsOpen(false); setJumpToDate(date); }}
      />

      <HolidayRequestDialog
        open={holidayDialogOpen}
        onClose={() => setHolidayDialogOpen(false)}
        teamMembers={teamMembers}
        editMode={editMode}
        authPassword={authPassword}
      />

      {editMode && (
        <TeamStatsDialog
          open={teamStatsOpen}
          onClose={() => setTeamStatsOpen(false)}
          authPassword={authPassword}
        />
      )}

      {editMode && (
        <ApiAccessDialog
          open={apiAccessOpen}
          onClose={() => setApiAccessOpen(false)}
          authPassword={authPassword}
        />
      )}

      {editMode && (
        <AddNoteDialog
          open={addNoteOpen}
          onClose={() => setAddNoteOpen(false)}
          onCreated={fetchNotes}
          authPassword={authPassword}
        />
      )}
      
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
